'use client'
import Image from '@/components/ui/MediaImage';
import {motion} from "framer-motion";
import Link from "next/link";
import { transformMediaUrl } from "@/lib/media-url-transformer";

interface HomeAboutCTAProps {
    bg_map?: boolean | null;
    subTitle?: string;
    title?: string;
    description?: string;
    /** Sol görsel üzerindeki dikey kırmızı şerit metni */
    ribbonText?: string;
    /** Beyaz kutu: yıl sayısı (örn. 28) */
    experienceYears?: string;
    /** Beyaz kutu: yıl altı satır (örn. year of experience) */
    experienceSubtitle?: string;
    leftImage?: number | string | { mediaId?: number; url?: string; alt?: string } | null;
    rightImage?: number | string | { mediaId?: number; url?: string; alt?: string } | null;
    moreAboutUsPage?: { type?: 'page' | 'custom'; id?: number; slug?: string; title?: string; url?: string } | null;
    trustedTitle?: string;
    trustedDescription?: string;
    /** İkinci sütun (rezervasyon) başlığı */
    bookTitle?: string;
    bookDescription?: string;
}

function coerceBgMap(value: boolean | string | null | undefined): boolean {
    if (value === true || value === false) return value;
    if (value === null || value === undefined || value === '') return false;
    const s = String(value).toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
}

/** block_json'da yanlışlıkla HTML/sınıf parçası kaydedilmiş şerit metnini gösterimde düzelt */
function plainTextFromMaybeHtml(s: string | undefined | null): string {
    if (s == null || s === '') return '';
    const t = String(s);
    if (!/[<>]/.test(t) && !/\bbefore:[\w-]/.test(t)) return t;
    return t.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default function HomeAboutCTA({ 
    bg_map,
    subTitle = 'Explore the world',
    title = 'Great opportunity for adventure & travels',
    description = 'Alsero Travel was founded in 1996 and is a Turkish Travel Agencies Association member with license number 3142 -A',
    ribbonText = '25,000+ Customers Worldwide',
    experienceYears = '28',
    experienceSubtitle = 'year of experience',
    leftImage,
    rightImage,
    moreAboutUsPage,
    trustedTitle = 'Trusted travel guide',
    trustedDescription = 'Alsero Travel was founded in 1996 and is a Turkish Travel Agencies ',
    bookTitle = 'Book with confidence',
    bookDescription = 'Alsero Travel was founded in 1996 and is a Turkish Travel Agencies '
}: HomeAboutCTAProps) {
    const showBgMap = coerceBgMap(bg_map as boolean | string | null | undefined);

    // Image URL helper
    const getImageUrl = (imageProp: number | string | { mediaId?: number; url?: string } | null | undefined, fallback: string): string => {
        if (!imageProp) return fallback;
        
        if (typeof imageProp === 'object' && 'url' in imageProp && imageProp.url) {
            return transformMediaUrl(imageProp.url, fallback);
        }
        if (typeof imageProp === 'string') {
            return transformMediaUrl(imageProp, fallback);
        }
        return fallback;
    };

    const leftImageUrl = getImageUrl(leftImage, '/dinamic/about-01.png');
    const rightImageUrl = getImageUrl(rightImage, '/dinamic/about-02.png');
    const ribbonTextDisplay = plainTextFromMaybeHtml(ribbonText);

    // More About Us link - slug varsa kullan, yoksa #
    // Link selector trait'inden gelen obje formatı: { type: 'page'|'custom', id?: number, slug?: string, title?: string, url?: string }
    const moreAboutUsHref = moreAboutUsPage?.type === 'custom' 
        ? (moreAboutUsPage?.url || '#')
        : (moreAboutUsPage?.slug ? `/${moreAboutUsPage.slug}` : '#');
    
    // Link text'i - title varsa kullan, yoksa default
    const moreAboutUsText = moreAboutUsPage?.title || 'More About Us';
    const titleVariants = {
        hidden: {
            opacity: 0,
            y: 30
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut" as const
            }
        }
    }

    return (
        <section
            className={`mb-16 py-16 sm:mb-24 sm:py-24 md:mb-28 md:py-32 lg:mb-[100px] xl:py-[200px] ${showBgMap ? "bg-white bg-alsero-map-decor" : ''}`}
        >
            <div className="mx-auto w-full min-w-0 max-w-9/12 px-4 sm:px-6">
                <div className="grid grid-cols-12 items-start justify-center gap-y-10 lg:gap-y-0">
                    <div className="relative col-span-12 h-auto min-h-0 w-full pb-8 lg:col-span-6 lg:pb-[110px] xl:col-span-5">
                        {/* Mobilde üst üste; lg+ masaüstü: 490×590 görseller + üst üste binme */}
                        <div className="relative mx-auto w-full max-w-[490px] lg:mx-0">
                            <div className="relative aspect-[490/590] w-full overflow-hidden after:absolute after:inset-x-0 after:-bottom-[30px] after:z-0 after:h-[56px] after:bg-top after:bg-repeat-x after:bg-[length:auto_56px] after-alsero-white-line-56 lg:aspect-auto lg:h-[590px] lg:w-[490px]">
                                <Image
                                    src={leftImageUrl}
                                    fill
                                    alt="about-01"
                                    className="object-cover"
                                    sizes="(max-width: 1023px) min(100vw, 490px), 490px"
                                    unoptimized={leftImageUrl.startsWith('/api/media/proxy/')}
                                    quality={90}
                                />
                            </div>
                            <div className="absolute -left-2 top-0 z-10 h-full max-h-[450px] w-[64px] sm:-left-3 sm:w-[80px] lg:-left-3 lg:h-[450px] lg:w-[90px]">
                                <div
                                    data-prop-path="ribbonText"
                                    data-prop-allow-html="false"
                                    className="absolute inset-0 flex items-center justify-center bg-alsero-first px-1 text-base font-bold tracking-wide text-white [writing-mode:vertical-rl] rotate-180 sm:text-xl lg:text-2xl"
                                >
                                    {ribbonTextDisplay}
                                </div>
                                <span
                                    className="pointer-events-none absolute inset-0 h-full w-full rotate-180 before:absolute before:-bottom-[44px] before:left-0 before:border-b-[45px] before:border-l-[45px] before:border-r-[45px] before:border-b-transparent before:border-l-alsero-first before:border-r-alsero-first before:content-['']"
                                    aria-hidden
                                />
                            </div>
                        </div>
                        <div className="relative mx-auto mt-8 aspect-[490/590] w-full max-w-[490px] overflow-hidden after:absolute after:inset-x-0 after:-bottom-[30px] after:z-0 after:h-[56px] after:bg-top after:bg-repeat-x after:bg-[length:auto_56px] after-alsero-white-line-56 lg:absolute lg:right-0 lg:top-[33.333333%] lg:mt-0 lg:aspect-auto lg:h-[590px] lg:w-[490px]">
                            <Image
                                src={rightImageUrl}
                                fill
                                alt="about-02"
                                className="object-cover"
                                sizes="(max-width: 1023px) min(100vw, 490px), 490px"
                                unoptimized={rightImageUrl.startsWith('/api/media/proxy/')}
                                quality={90}
                            />
                        </div>
                        <h3 className="relative z-20 mx-auto mt-8 flex h-auto min-h-[160px] w-full max-w-[216px] flex-col items-center justify-center bg-white px-6 py-6 text-center text-base font-medium text-alsero-second shadow-sm sm:min-h-[180px] sm:px-8 sm:text-lg lg:absolute lg:bottom-0 lg:right-1/2 lg:mt-0 lg:min-h-[200px] lg:translate-y-1/2 lg:px-8 lg:shadow-none">
                            <span data-prop-path="experienceYears" className="text-4xl font-bold text-alsero-first sm:text-5xl">
                                {experienceYears}
                            </span>
                            <span data-prop-path="experienceSubtitle">{experienceSubtitle}</span>
                        </h3>
                    </div>
                    <div className="col-span-12 h-full lg:col-span-6 xl:col-span-5 xl:col-start-8">
                        <div className="flex flex-col gap-10">
                            <motion.div className="w-full" variants={titleVariants}>
                                {/* data-prop-path her zaman DOM'da olmalı (editör kayıt / trait senkron); boş metin de izlenebilir */}
                                <span
                                    data-prop-path="subTitle"
                                    className="font-display font-medium text-alsero-first text-xl sm:text-2xl md:text-3xl lg:text-4xl"
                                >
                                    {subTitle ?? ''}
                                </span>
                                <h2
                                    data-prop-path="title"
                                    className="font-bold text-alsero-text-dark text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight sm:leading-tight md:leading-snug"
                                >
                                    {title}
                                </h2>
                                <p
                                    data-prop-path="description"
                                    className="mt-6 sm:mt-8 md:mt-10 w-full sm:w-10/12 lg:w-8/12 text-alsero-dark-gray text-sm sm:text-base md:text-lg leading-relaxed"
                                >
                                    {description ?? ''}
                                </p>
                            </motion.div>
                            <div className="grid grid-cols-12 justify-between gap-y-8 sm:gap-y-0">
                                <div className="col-span-12 sm:col-span-6 lg:col-span-5">
                                    <div className="flex flex-col gap-3 justify-between items-start">
                                        <svg width="67" height="58" viewBox="0 0 67 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_2093_6226)">
                                                <ellipse cx="47.6304" cy="12.6529" rx="11.5357" ry="11.5357" fill="#EDEEEE"/>
                                                <path d="M39.819 13.7708C39.819 10.3153 37.0077 7.50391 33.5521 7.50391C30.0965 7.50391 27.2852 10.3153 27.2852 13.7708C27.2852 17.2264 30.0965 20.0378 33.5521 20.0378C37.0077 20.0378 39.819 17.2264 39.819 13.7708ZM33.5521 16.9043C31.8243 16.9043 30.4186 15.4986 30.4186 13.7708C30.4186 12.0431 31.8243 10.6374 33.5521 10.6374C35.2799 10.6374 36.6856 12.0431 36.6856 13.7708C36.6856 15.4986 35.2799 16.9043 33.5521 16.9043Z" fill="#E31E24"/>
                                                <path d="M53.1826 15.6403C51.8948 13.6655 49.2651 13.0038 47.1959 14.134L36.3869 20.0374C35.3691 20.0374 17.4164 20.0374 16.3697 20.0374V5.83236C16.3697 4.9671 15.6682 4.26562 14.8029 4.26562H2.00521C1.13996 4.26562 0.438477 4.9671 0.438477 5.83236V15.3372C0.438477 16.2025 1.13996 16.904 2.00521 16.904H13.2361V56.1768C13.2361 57.0421 13.9376 57.7435 14.8028 57.7435C15.6681 57.7435 16.3696 57.0421 16.3696 56.1768V29.4378H25.77V53.0433C25.77 57.1168 30.604 59.2316 33.6037 56.5438C36.6003 59.229 41.4373 57.1229 41.4373 53.0433V28.7203L51.8562 21.9327C53.9621 20.5608 54.5558 17.7459 53.1826 15.6403ZM3.57195 13.7704V7.39899H13.2361V13.7704H3.57195ZM36.7371 54.61C35.8732 54.61 35.1704 53.9071 35.1704 53.0432C35.1704 51.8281 35.1704 44.6753 35.1704 43.5384C35.1704 42.6731 34.4689 41.9716 33.6037 41.9716C32.7384 41.9716 32.0369 42.6731 32.0369 43.5384V53.0432C32.0369 53.9071 31.3341 54.61 30.4702 54.61C29.6063 54.61 28.9035 53.9071 28.9035 53.0432V38.8382H38.3039V53.0432C38.3039 53.9071 37.601 54.61 36.7371 54.61ZM50.1459 19.3071L39.0154 26.5583C38.5716 26.8475 38.3039 27.3413 38.3039 27.871V35.7047H28.9035V27.871C28.9035 27.0057 28.202 26.3043 27.3367 26.3043H16.3696V23.1708C37.7054 23.1036 36.8874 23.3344 37.5377 22.9791L48.6978 16.884C49.3407 16.5329 50.1578 16.7384 50.5579 17.352C50.9846 18.0061 50.8004 18.8809 50.1459 19.3071Z" fill="#E31E24"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_2093_6226">
                                                    <rect width="66.0682" height="57.6786" fill="white" transform="translate(0.438477 0.0683594)"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <h3 data-prop-path="trustedTitle" className="font-bold text-lg text-alsero-second">{trustedTitle}</h3>
                                        <p data-prop-path="trustedDescription" className="text-sm text-alsero-second">{trustedDescription}</p>
                                    </div>
                                </div>
                                <div className="col-span-12 sm:col-span-6 sm:col-start-7 lg:col-span-5">
                                    <div className="flex flex-col gap-3 justify-between items-start h-full">
                                        <svg width="46" height="45" viewBox="0 0 46 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="33.6783" cy="11.9927" r="11.5357" fill="#EDEEEE"/>
                                            <mask id="mask0_2093_6232" maskUnits="userSpaceOnUse" x="0" y="2" width="43" height="43">
                                                <path d="M0.120117 2.55488H42.0682V44.5029H0.120117V2.55488Z" fill="white"/>
                                            </mask>
                                            <g mask="url(#mask0_2093_6232)">
                                                <path d="M21.0938 26.8056V43.2734H40.8388V15.4173H35.923" stroke="#E31E24" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M6.26442 15.4173H1.34863V43.2734H21.0937V26.8875" stroke="#E31E24" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M28.3925 12.1401H35.923V38.3577H27.914C24.8196 38.3577 22.0722 40.3377 21.0938 43.2734" stroke="#E31E24" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M13.7961 12.1401H6.26562V38.3577H14.2747C17.3691 38.3577 20.1164 40.3377 21.0949 43.2734" stroke="#E31E24" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M21.094 3.78329C15.5331 3.78329 11.9819 9.71444 14.6079 14.6162L21.094 26.7236L27.5802 14.6162C30.2061 9.71444 26.6549 3.78329 21.094 3.78329Z" stroke="#E31E24" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M23.5515 11.1573C23.5515 12.5148 22.451 13.6152 21.0936 13.6152C19.7362 13.6152 18.6357 12.5148 18.6357 11.1573C18.6357 9.79993 19.7362 8.69945 21.0936 8.69945C22.451 8.69945 23.5515 9.79993 23.5515 11.1573Z" stroke="#E31E24" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                            </g>
                                        </svg>
                                        <h3 data-prop-path="bookTitle" className="font-bold text-lg text-alsero-second">{bookTitle}</h3>
                                        <p data-prop-path="bookDescription" className="text-sm text-alsero-second">{bookDescription}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row flex-wrap gap-4 sm:gap-7">
                                <Link 
                                    href={moreAboutUsHref} 
                                    data-prop-path="moreAboutUsPage" 
                                    className="inline-flex items-center justify-center rounded-[5px] bg-alsero-first px-6 py-3 text-sm font-semibold uppercase tracking-[0.05em] text-white sm:px-[45px] sm:py-[17px]"
                                >
                                    {moreAboutUsText}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
     )
}